import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Upload as UploadIcon } from 'lucide-react';
import { Button } from '../../ui/atoms/Button';
import { SegmentedButton } from '../../ui/atoms/SegmentedButton';
import { FileUpload, SUPPORTED_EXTENSIONS } from '../../ui/FileUpload';
import { useVoiceDictation } from '../../../hooks/useVoiceDictation';
import { MicButton } from './MicButton';

interface InputStateProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

type InputMode = 'upload' | 'describe';

function pickDefaultMode(): InputMode {
  if (typeof window === 'undefined') return 'upload';
  return window.matchMedia('(max-width: 767px)').matches ? 'describe' : 'upload';
}

export const InputState: React.FC<InputStateProps> = ({
  file,
  onFileChange,
  description,
  onDescriptionChange,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const [mode, setMode] = useState<InputMode>(pickDefaultMode);
  const dictation = useVoiceDictation();
  // Snapshot of whatever the user had typed before this dictation session began.
  // Dictation is appended on top of it so typed and dictated text don't fight.
  const baseRef = useRef('');

  // While recording, push base + live transcript into the textarea.
  // When not recording, leave description fully under the user's control.
  useEffect(() => {
    if (!dictation.isRecording) return;
    const live = [dictation.transcript, dictation.interim].filter(Boolean).join(' ').trim();
    const combined = [baseRef.current, live].filter(Boolean).join(' ').trim();
    onDescriptionChange(combined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictation.transcript, dictation.interim, dictation.isRecording]);

  // Stop the mic if user switches mode while recording.
  useEffect(() => {
    if (mode === 'upload' && dictation.isRecording) {
      void dictation.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const toggleMic = async () => {
    if (dictation.isRecording) {
      await dictation.stop();
    } else {
      baseRef.current = description; // anchor on whatever is already typed
      dictation.reset();
      await dictation.start();
    }
  };

  const canSubmit =
    mode === 'upload' ? file !== null : description.trim().length > 0;

  return (
    <div className="flex flex-col gap-5 flex-1 min-h-0">
      <SegmentedButton
        aria-label={t('create_job_modal.input_mode_aria_label', {
          defaultValue: 'Job description input mode',
        })}
        size="md"
        appearance="raised"
        intent="secondary"
        equalWidth
        indicator="slide"
        value={mode}
        onValueChange={(next) => setMode(next as InputMode)}
        options={[
          {
            value: 'upload',
            icon: <UploadIcon />,
            label: (
              <>
                <span className="sm:hidden">{t('create_job_modal.upload_short')}</span>
                <span className="hidden sm:inline">{t('create_job_modal.upload_file')}</span>
              </>
            ),
          },
          {
            value: 'describe',
            icon: <Mic />,
            label: (
              <>
                <span className="sm:hidden">{t('create_job_modal.describe_short')}</span>
                <span className="hidden sm:inline">{t('create_job_modal.describe_role')}</span>
              </>
            ),
          },
        ]}
      />

      <div className="flex-1 min-h-0 flex items-center">
        {mode === 'upload' ? (
          <FileUpload
            className="w-full"
            onFilesSelected={(files) => onFileChange(files[0] ?? null)}
            selectedFiles={file ? [file] : []}
            onFileRemove={() => onFileChange(null)}
            acceptedFormats={SUPPORTED_EXTENSIONS}
            multiple={false}
            variant="side-by-side"
            iconColor="text-dark-invirtus"
            buttonColor="bg-purple hover:bg-purple/60"
            buttonTextColor="text-dark-invirtus"
            buttonText={t('create_job_modal.upload_job_description')}
            description={t('create_job_modal.file_types')}
            dragHint={t('common:file_upload.drag_hint')}
            id="job-description-upload"
            showFileList
          />
        ) : (
          <div className="space-y-2 w-full">
            <div className="relative">
              <textarea
                id="job-description-text"
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 pr-16 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-purple focus:border-purple font-body text-sm resize-none disabled:bg-gray-50"
                placeholder={t('create_job_modal.description_placeholder')}
                disabled={dictation.isConnecting || dictation.isRecording}
              />
              <div className="absolute right-3 bottom-3">
                <MicButton
                  isRecording={dictation.isRecording}
                  isConnecting={dictation.isConnecting}
                  level={dictation.level}
                  onToggle={toggleMic}
                />
              </div>
            </div>

            {dictation.error && (
              <p className="text-xs text-red-500 font-body">{dictation.error}</p>
            )}
            {!dictation.error && dictation.isRecording && (
              <p className="text-xs text-purple font-body">
                {t('create_job_modal.listening_hint')}
              </p>
            )}
            {!dictation.error && !dictation.isRecording && (
              <p className="text-xs text-gray-500 font-body">
                {t('create_job_modal.typing_hint')}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-4 mt-auto border-t border-gray-200">
        <Button
          label={t('create_job_modal.cancel')}
          onClick={async () => {
            if (dictation.isRecording) await dictation.stop();
            onCancel();
          }}
          type="outlined"
          intent="secondary"
          className="w-full sm:w-auto"
        />
        <Button
          label={t('create_job_modal.analyze')}
          onClick={async () => {
            // Stop dictation before submitting so we don't leave a WS open.
            if (dictation.isRecording) await dictation.stop();
            onSubmit();
          }}
          type="contained"
          intent="primary"
          disabled={!canSubmit}
          className="w-full sm:w-auto"
        />
      </div>
    </div>
  );
};
